import { Badge } from '@fluentui/react-components';

export type StatusType = 'Active' | 'Ready' | 'Failed' | 'Inactive' | 'Provisioning';

type StatusBadgeProps = {
  status: StatusType | string;
  size?: 'small' | 'medium';
};

const statusConfig: Record<StatusType, { color: 'success' | 'danger' | 'warning' | 'informative'; appearance: 'filled' | 'tint' }> = {
  Active: { color: 'success', appearance: 'filled' },
  Ready: { color: 'success', appearance: 'filled' },
  Failed: { color: 'danger', appearance: 'filled' },
  Inactive: { color: 'informative', appearance: 'filled' },
  Provisioning: { color: 'warning', appearance: 'tint' },
};

const getStatusType = (status: string): StatusType => {
  if (status in statusConfig) {
    return status as StatusType;
  }
  return 'Inactive';
};

/**
 * Maps API provisioning states to display status types.
 * Use this to convert backend state strings to StatusBadge-compatible values.
 */
export const mapProvisioningState = (state: string): StatusType => {
  switch (state.toLowerCase()) {
    case 'succeeded':
    case 'ready':
    case 'running':
      return 'Ready';
    case 'failed':
    case 'error':
      return 'Failed';
    case 'provisioning':
      return 'Provisioning';
    case 'deleted':
    case 'stopped':
    case 'disabled':
      return 'Inactive';
    default:
      return state as unknown as StatusType; // Fallback for unrecognized states, will be treated as Inactive
  }
};

export const StatusBadge = ({ status, size = 'medium' }: StatusBadgeProps) => {
  const resolved = getStatusType(status);
  const config = statusConfig[resolved];

  return (
    <Badge
      appearance={config.appearance}
      color={config.color}
      size={size}
      aria-label={`Status: ${resolved}`}
    >
      {resolved}
    </Badge>
  );
};
